const routeCollator = new Intl.Collator(undefined, {
	numeric: true,
	sensitivity: "base",
});

const normalizeText = (value) =>
	`${value ?? ""}`
		.trim()
		.replace(/\s+/g, " ")
		.toLowerCase();

export const toTitleCase = (value) =>
	`${value ?? ""}`
		.trim()
		.replace(/\s+/g, " ")
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");

const getRoutePriority = (option = {}) => {
	if (option?.is_selected) {
		return 0;
	}

	if (option?.is_locked) {
		return 2;
	}

	return 1;
};

const getIdentityKey = (target = {}) => {
	const id = target?.id ?? "";
	const name = normalizeText(target?.name);

	if (id === "" || !name) {
		return null;
	}

	return `${id}::${name}`;
};

const getTargetValue = (target = {}, fallbackType = "driver") => {
	const id = target?.id;
	if (id === null || id === undefined || id === "") {
		return null;
	}

	const type = target?.type || fallbackType;
	return `${type}:${id}`;
};

const dedupeTargetsByValue = (targets = [], fallbackType = "driver") => {
	const seen = new Set();

	return (Array.isArray(targets) ? targets : []).filter((target) => {
		const key = getTargetValue(target, fallbackType);
		if (!key || seen.has(key)) {
			return false;
		}

		seen.add(key);
		return true;
	});
};

export const getSelectedRoutesFromOptions = (options = []) =>
	(Array.isArray(options) ? options : [])
		.filter((option) => option?.is_selected)
		.map((option) => option.route);

export const getRouteStatusLabel = (option) => {
	if (option?.is_selected) {
		const pickerName = option?.owner_name || option?.selected_pharmacist_name;
		return pickerName ? `Picked by ${toTitleCase(pickerName)}` : "Picked";
	}

	if (option?.is_locked) {
		return option?.owner_name ? `Locked by ${toTitleCase(option.owner_name)}` : "Locked";
	}

	return "Available";
};

export const sortRouteOptions = (options = []) =>
	[...(Array.isArray(options) ? options : [])].sort((left, right) => {
		const priorityDelta = getRoutePriority(left) - getRoutePriority(right);
		if (priorityDelta !== 0) {
			return priorityDelta;
		}

		return routeCollator.compare(`${left?.route ?? ""}`, `${right?.route ?? ""}`);
	});

export const getVisibleSelectedPharmacistName = (option = {}) => {
	const pharmacistName = normalizeText(option?.selected_pharmacist_name);
	if (!pharmacistName) {
		return "";
	}

	const ownerName = normalizeText(option?.owner_name);
	const sameDisplayName = ownerName && ownerName === pharmacistName;
	const sameNumericId =
		option?.selected_pharmacist_id !== null &&
		option?.selected_pharmacist_id !== undefined &&
		option?.delivery_driver_id !== null &&
		option?.delivery_driver_id !== undefined &&
		String(option.selected_pharmacist_id) === String(option.delivery_driver_id);

	return sameDisplayName || (sameNumericId && (!ownerName || ownerName === pharmacistName))
		? ""
		: toTitleCase(option?.selected_pharmacist_name);
};

export const getActivePharmacistOptions = (pharmacists = []) => {
	const seen = new Set();

	return (Array.isArray(pharmacists) ? pharmacists : []).filter((pharmacist) => {
		if (![1, "1", true].includes(pharmacist?.active)) {
			return false;
		}

		const identityKey = getIdentityKey(pharmacist) || `${pharmacist?.id ?? ""}`;
		if (seen.has(identityKey)) {
			return false;
		}

		seen.add(identityKey);
		return true;
	});
};

// Returns the grouped target lists used by the handover tab UI:
//   teammates            — assistants + supervisors + drivers
//   assistantPharmacists — pharmacists shown by pharmacist name, but routed to the linked delivery-app user
//   pharmacists          — instore pharmacist-role users for instore handover
export const buildHandoverTargetOptions = (handoverTargets = {}) => {
	const byName = (left, right) => routeCollator.compare(left?.name ?? "", right?.name ?? "");

	const instorePharmacists = dedupeTargetsByValue(
		handoverTargets?.instore_pharmacist || handoverTargets?.pharmacists || [],
		"pharmacy_user"
	)
		.map((target) => ({
			...target,
			value: getTargetValue(target, "pharmacy_user"),
			label: target.name,
		}))
		.sort(byName);

	const pharmacistKeys = new Set(instorePharmacists.map(getIdentityKey).filter(Boolean));

	const assistants = dedupeTargetsByValue(handoverTargets?.assistants || [])
		.filter((target) => {
			const key = getIdentityKey(target);
			return key ? !pharmacistKeys.has(key) : true;
		})
		.map((target) => ({
			...target,
			value: getTargetValue(target),
			label: target.name,
		}))
		.sort(byName);

	// Exclude anyone already in assistants so the same person doesn't appear twice.
	const assistantKeys = new Set(assistants.map(getIdentityKey).filter(Boolean));

	const supervisors = dedupeTargetsByValue(handoverTargets?.supervisors || [])
		.filter((target) => {
			const key = getIdentityKey(target);
			return key ? !assistantKeys.has(key) && !pharmacistKeys.has(key) : true;
		})
		.map((target) => ({
			...target,
			value: getTargetValue(target),
			label: target.name,
		}))
		.sort(byName);

	const teammateKeys = new Set([
		...assistantKeys,
		...supervisors.map(getIdentityKey).filter(Boolean),
	]);

	const drivers = dedupeTargetsByValue(handoverTargets?.drivers || [])
		.filter((target) => {
			const key = getIdentityKey(target);
			return key ? !teammateKeys.has(key) && !pharmacistKeys.has(key) : true;
		})
		.map((target) => ({
			...target,
			value: getTargetValue(target),
			label: target.name,
		}))
		.sort(byName);

	const teammateTargets = dedupeTargetsByValue([...assistants, ...supervisors, ...drivers]).sort(byName);
	const assistantPharmacists = dedupeTargetsByValue(
		(handoverTargets?.pharmacists || [])
			.filter((target) => target?.assistant_delivery_driver_id)
			.map((target) => {
				return {
					...target,
					id: target?.assistant_delivery_driver_id,
					type: "driver",
					linkedPharmacistId: target?.id,
					value: getTargetValue({
						id: target?.assistant_delivery_driver_id,
						type: "driver",
					}, "driver"),
					label: target.name,
				};
			})
	).sort(byName);

	return {
		teammates: teammateTargets,
		assistantPharmacists,
		pharmacists: instorePharmacists,
	};
};
